import React, { useCallback, useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '../../utils';
import Icon from './Icon';
import './MeetQrSheet.css';

/* "Show my QR" — the networking code an attendee holds up so somebody else can scan it with
   their plain phone camera. Backend: GET /api/event/:id/meet/qr.

   Two things here are deliberate and should not be tidied away:

   ★ The QR encodes a full URL, not a token. That is the whole reason no scanner of ours is
     needed: iOS and Android only offer a tappable banner for a URL.

   ★ The consent line IS the consent record, and it names the fields that will actually be sent
     (built from the server's `share` block, never a hardcoded list). Replacing it with a generic
     sentence would leave us claiming agreement to something nobody was shown. */
const NUDGE_SKIPPED_KEY = 'meetNudgeSkipped';

const MeetQrSheet = ({ eventId, onClose }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linkedin, setLinkedin] = useState('');
  const [instagram, setInstagram] = useState('');
  const [saving, setSaving] = useState(false);
  const [skipped, setSkipped] = useState(() => {
    try { return window.sessionStorage.getItem(NUDGE_SKIPPED_KEY) === '1'; } catch (_) { return false; }
  });

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      setData(await apiRequest('GET', `/event/${eventId}/meet/qr`));
      setError(null);
    } catch (err) {
      setError(t(err.message, { defaultValue: t('somethingWentWrong') }));
    } finally {
      setLoading(false);
    }
  }, [eventId, t]);

  useEffect(() => { load(); }, [load]);

  const skip = () => {
    try { window.sessionStorage.setItem(NUDGE_SKIPPED_KEY, '1'); } catch (_) { /* private mode */ }
    setSkipped(true);
  };

  const saveSocials = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // The existing socials editor endpoint — it already validates exactly these fields.
      const current = await apiRequest('GET', '/connections');
      await apiRequest('PUT', '/connections', {
        ...current,
        linkedin: linkedin.trim() || current.linkedin,
        instagram: instagram.trim() || current.instagram,
      });
      await load();
      setSkipped(true);
    } catch (err) {
      setError(t(err.message, { defaultValue: t('somethingWentWrong') }));
    } finally {
      setSaving(false);
    }
  };

  const shared = data ? Object.keys(data.share || {}) : [];
  const sharedLabel = shared.map((k) => t(`meetField_${k}`, { defaultValue: k })).join(', ');

  return (
    <div className="meet-sheet" role="dialog" aria-label={t('meetTitle')}>
      <div className="meet-sheet__panel">
        <button type="button" className="meet-sheet__close" onClick={onClose} aria-label={t('close')}>
          <Icon name="cross" />
        </button>

        {loading && <div className="meet-sheet__empty">{t('loading')}</div>}

        {!loading && error && <div className="meet-sheet__empty">{error}</div>}

        {!loading && !error && data && data.nudge === 'socials' && !skipped && (
          <form className="meet-sheet__nudge" onSubmit={saveSocials}>
            <div className="meet-sheet__title">{t('meetNudgeTitle')}</div>
            <div className="meet-sheet__sub">{t('meetNudgeBody')}</div>
            <label className="meet-sheet__label">{t('meetAddLinkedin')}</label>
            <input
              className="meet-sheet__input"
              type="url"
              value={linkedin}
              onChange={(e) => setLinkedin(e.target.value)}
              placeholder="https://linkedin.com/in/…"
            />
            <label className="meet-sheet__label">{t('meetAddInstagram')}</label>
            <input
              className="meet-sheet__input"
              type="text"
              value={instagram}
              onChange={(e) => setInstagram(e.target.value)}
              placeholder="@…"
            />
            <button className="meet-sheet__primary" type="submit" disabled={saving}>
              {t('save')}
            </button>
            <button className="meet-sheet__skip" type="button" onClick={skip}>
              {t('meetSkip')}
            </button>
          </form>
        )}

        {!loading && !error && data && (data.nudge !== 'socials' || skipped) && (
          <div className="meet-sheet__qr-wrap">
            <div className="meet-sheet__title">{t('meetShowQr')}</div>
            <div className="meet-sheet__consent">
              {t('meetConsentLine', { fields: sharedLabel })}
            </div>
            <div className="meet-sheet__qr">
              <QRCodeSVG value={data.meet_url} size={240} level="M" includeMargin={false} />
            </div>
            <div className="meet-sheet__sub">{t('meetShareHint')}</div>
            {data.connections_here > 0 && (
              <div className="meet-sheet__count">
                <Icon name="user-check" theme="primary" className="me-1" />
                {t('meetMetHere', { count: data.connections_here })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetQrSheet;
